import { decodeRegionNameURI } from 'pages/Map/regionHelper';
import { Dispatch, SetStateAction, useCallback, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';

type MapParams = {
    region: string;
    zoneName: string;
    userId: string;
};
type MapQueryParams = {
    position: {
        x: number;
        y: number;
    };
    scale: number;
    station: string;
    subZone: string;
};
type MapQueryParamsInput = Omit<MapQueryParams, 'position'> & {
    pos_x: number;
    pos_y: number;
};
type AdditionalProps = {
    hasInitialMapParams: boolean;
    initialSelectedStation: string;
    setInitialSelectedStation: Dispatch<SetStateAction<string>>;
    toMapQueryString: (newParams: Partial<MapQueryParamsInput>) => string;
};

export const useMapParams = (): MapParams & MapQueryParams & AdditionalProps => {
    const location = useLocation();
    const { region = '', zoneName = '', userId = '' } = useParams();
    const queryParams = new URLSearchParams(location.search);
    const station = queryParams.get('station') || '';
    const subZone = queryParams.get('subZone') || '';
    const x = Number(queryParams.get('pos_x') || 0);
    const y = Number(queryParams.get('pos_y') || 0);
    const scale = Number(queryParams.get('scale') || 0);
    const [hasInitialMapParams] = useState(!!x || !!y || !!scale);
    const [initialSelectedStation, setInitialSelectedStation] = useState(station);

    const toMapQueryString = useCallback(
        (newParams: Partial<MapQueryParamsInput>) => {
            const params = new URLSearchParams(location.search);
            Object.keys(newParams).forEach(key => {
                const value = newParams[key as keyof MapQueryParamsInput];
                if (value) {
                    params.set(key, value.toString());
                } else {
                    params.delete(key);
                }
            });
            return params.toString();
        },
        [location.search]
    );

    return {
        region: decodeRegionNameURI(region),
        zoneName,
        userId,
        station,
        subZone,
        position: { x, y },
        scale,
        initialSelectedStation,
        setInitialSelectedStation,
        hasInitialMapParams,
        toMapQueryString
    };
};
